import {
  Avatar,
  Flex,
  ScrollArea,
  Text,
  Tooltip,
  useMantineTheme,
  Modal,
} from "@mantine/core";
import { AddCircle, Profile } from "iconsax-react";
import { IProductOrganization } from "../../interfaces/Products/IProductDetail";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import Search from "../Search";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

type IProps = {
  organizations?: IProductOrganization[];
  hasColor?: boolean;
};

export default function BrokersCards({
  organizations,
  hasColor = true,
}: IProps) {
  const { t } = useTranslation();
  const isLargerThan991 = useMediaQuery("(max-width: 991px)");
  const theme = useMantineTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleCards, setVisibleCards] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 200);

  useLayoutEffect(() => {
    let raf: number;

    const calculateVisibleCards = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        if (containerWidth === 0) {
          // Tenta de novo no próximo frame
          raf = requestAnimationFrame(calculateVisibleCards);
          return;
        }
        const cardWidth = hasColor ? 104 : 58;
        const maxCards = Math.floor(containerWidth / cardWidth);
        setVisibleCards(maxCards);
      }
    };

    const observer = new window.ResizeObserver(calculateVisibleCards);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Chama uma vez para garantir
    calculateVisibleCards();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [organizations, hasColor]);

  const normalize = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const filteredOrganizations = useMemo(() => {
    if (!debouncedSearch) return organizations || [];
    return (organizations || []).filter((organization) =>
      normalize(organization.name).includes(normalize(debouncedSearch))
    );
  }, [debouncedSearch, organizations]);

  return !!organizations && organizations.length > 0 ? (
    <>
      <Modal
        size="all"
        opened={open}
        onClose={() => setOpen(false)}
        transitionProps={{
          exitDuration: 600,
          transition: "slide-up",
          duration: 600,
          timingFunction: "ease",
        }}
        withCloseButton={false}
        centered
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{
          content: {
            borderRadius: "12px",
            minWidth: "fit-content",
            maxWidth: "fit-content",
          },
          inner: {
            zIndex: 1000,
          },
          overlay: { zIndex: 999 },
          header: {
            marginBottom: "0",
            background: "#F0F0F0",
            padding: "20px 20px",
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            borderBottom: "1px solid #D7D7D7",
            gap: "24px",
            minWidth: "100%",
          },
          root: {
            minWidth: "100%",
            maxWidth: "800px",
          },
          close: {
            display: "flex",
            height: "28px",
            width: "28px",
            justifyContent: "center",
            alignItems: "center",
            svg: {
              height: "24px !important",
              width: "24px !important",
            },
          },
          body: {
            width: "90vw",
            minWidth: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "0",
            maxWidth: "800px",
          },
        }}
      >
        <Modal.Header>
          <Flex direction={"column"} w="100%" gap="16px">
            <Flex
              justify={"space-between"}
              align={"center"}
              w="100%"
              gap="24px"
            >
              <Text
                style={{
                  color: "#2F2F2F",
                  fontSize: "18px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {t("global.brokersCards.all")}
              </Text>
              <Flex
                justify={"space-between"}
                align={"center"}
                w="fit"
                gap="24px"
              >
                {isLargerThan991 ? null : (
                  <Search
                    placeholder={t("global.brokersCards.search")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClick={() => setSearch("")}
                  />
                )}
                <Modal.CloseButton />
              </Flex>
            </Flex>
            {isLargerThan991 ? (
              <Search
                placeholder={t("global.brokersCards.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={() => setSearch("")}
              />
            ) : null}
          </Flex>
        </Modal.Header>
        <Modal.Body>
          <Flex gap="16px" w="100%" wrap={"wrap"} p="15px 20px" rowGap="24px">
            {filteredOrganizations.map((organization, index) => {
              return (
                <Tooltip
                  label={organization?.name}
                  key={`${organization?.id}-${index}`}
                  zIndex={10}
                  position="bottom"
                  withArrow
                >
                  <span>
                    <Avatar
                      variant="outline"
                      size={hasColor ? 80 : 48}
                      color="basic.2"
                      radius={8}
                      src={organization?.icon}
                      alt={organization?.name}
                      style={{ transition: "all 0.5s linear" }}
                      styles={{
                        root: {
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `1px solid ${theme.colors.basic[2]}`,
                        },
                        placeholder: {
                          border: `unset`,
                        },
                        image: {
                          height: hasColor ? "70px" : "38px",
                          width: hasColor ? "70px" : "38px",
                        },
                      }}
                    >
                      <Profile />
                    </Avatar>
                  </span>
                </Tooltip>
              );
            })}
          </Flex>
        </Modal.Body>
      </Modal>
      <Flex
        ref={containerRef}
        h={hasColor ? "80px" : "48px"}
        w="100%"
        justify="end"
        align={"center"}
        pos={"relative"}
        sx={{ overflow: "hidden" }}
      >
        <Flex
          pos={"absolute"}
          w="100%"
          h="100%"
          align={"center"}
          gap={hasColor ? "24px" : "10px"}
        >
          {organizations?.slice(0, visibleCards).map((organization, index) => {
            return (
              <Tooltip.Floating
                label={organization?.name}
                key={`${organization?.id}-${index}`}
                zIndex={10}
              >
                <span>
                  <Avatar
                    variant="outline"
                    size={hasColor ? 80 : 48}
                    color="basic.2"
                    radius={8}
                    src={organization?.icon}
                    alt={organization?.name}
                    style={{ transition: "all 0.5s linear" }}
                    styles={{
                      root: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1px solid ${theme.colors.basic[2]}`,
                      },
                      placeholder: {
                        border: `unset`,
                      },
                      image: {
                        height: hasColor ? "70px" : "38px",
                        width: hasColor ? "70px" : "38px",
                      },
                    }}
                  >
                    <Profile />
                  </Avatar>
                </span>
              </Tooltip.Floating>
            );
          })}
        </Flex>
        {organizations.length > visibleCards ? (
          <Flex
            h={hasColor ? "80px" : "48px"}
            w={"170px"}
            px="24px"
            align={"center"}
            justify={"end"}
            sx={{ zIndex: 1 }}
            bg={
              hasColor
                ? "linear-gradient(90deg, rgba(0,65,255,0) 0%, rgba(255,255,255,0.7399334733893557) 5%, rgba(255,255,255,1) 30%)"
                : theme.colors.other[3]
            }
          >
            <Flex
              onClick={() => setOpen(true)}
              style={{
                cursor: "pointer",
              }}
              gap={"8px"}
            >
              <Text
                color={hasColor ? theme.colors.basic[4] : "white"}
                fz={"14px"}
                fw={600}
                style={{ whiteSpace: "nowrap" }}
                ff={"barlow"}
              >
                {t("global.brokersCards.see_all")}
              </Text>
              <AddCircle
                size={24}
                color={hasColor ? theme.colors.basic[2] : "white"}
              />
            </Flex>
          </Flex>
        ) : null}
      </Flex>
    </>
  ) : null;
}
